import React, { useState } from 'react';
import { Box, Typography, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';

interface AcuityData {
  rn: number | null;
  lpn: number | null;
  aide: number | null;
  total: number | null;
}

interface DaysBelowData {
  rn: number | null;
  lpn: number | null;
  cna: number | null;
}

interface AcuityExpandPanelProps {
  facilityAcuity: AcuityData;
  facilityAcuityHybrid?: AcuityData;
  stateAcuity: AcuityData;
  stateAcuityHybrid?: AcuityData;
  pctDaysBelowAcuity: DaysBelowData;
  pctDaysBelowAcuityHybrid?: DaysBelowData;
  primaryLabel?: string;       // defaults to "Facility"
  comparisonLabel?: string;    // defaults to "State Avg" (legend) / "State" (table)
}

const fmt = (v: number | null | undefined): string =>
  v != null ? `${v.toFixed(1)}%` : '-';

const MAX_PCT = 200;

interface BarRowData {
  label: string;
  facilityPct: number | null;
  statePct: number | null;
  pctDaysBelow: number | null;
}

const cellSx = {
  px: 1.5,
  py: 0.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  textAlign: 'right' as const,
};

const AcuityExpandPanel: React.FC<AcuityExpandPanelProps> = ({
  facilityAcuity,
  facilityAcuityHybrid,
  stateAcuity,
  stateAcuityHybrid,
  pctDaysBelowAcuity,
  pctDaysBelowAcuityHybrid,
  primaryLabel = 'Facility',
  comparisonLabel = 'State',
}) => {
  const [model, setModel] = useState<'anticipated' | 'hybrid'>('hybrid');

  const isHybrid = model === 'hybrid';
  const hasHybridFacility = facilityAcuityHybrid && (facilityAcuityHybrid.total != null || facilityAcuityHybrid.rn != null);
  const hasHybridState = stateAcuityHybrid && (stateAcuityHybrid.total != null || stateAcuityHybrid.rn != null);
  const hasHybridDays = pctDaysBelowAcuityHybrid && (pctDaysBelowAcuityHybrid.rn != null || pctDaysBelowAcuityHybrid.lpn != null);
  const acuity = isHybrid && hasHybridFacility ? facilityAcuityHybrid! : facilityAcuity;
  const stAcuity = isHybrid && hasHybridState ? stateAcuityHybrid! : stateAcuity;
  const daysBelow = isHybrid && hasHybridDays ? pctDaysBelowAcuityHybrid! : pctDaysBelowAcuity;

  const rows: BarRowData[] = [
    { label: 'RN', facilityPct: acuity.rn, statePct: stAcuity.rn, pctDaysBelow: daysBelow.rn },
    { label: 'LPN', facilityPct: acuity.lpn, statePct: stAcuity.lpn, pctDaysBelow: daysBelow.lpn },
    { label: 'CNA', facilityPct: acuity.aide, statePct: stAcuity.aide, pctDaysBelow: daysBelow.cna },
  ];

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
          Average Staffing as % of Acuity by Staffing Group
        </Typography>
        <ToggleButtonGroup
          value={model}
          exclusive
          onChange={(_, v) => v && setModel(v)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              py: 0.25, px: 1.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none',
              '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } },
            },
          }}
        >
          <ToggleButton value="hybrid">Hybrid</ToggleButton>
          <ToggleButton value="anticipated">Anticipated</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Two-column layout: bar chart left, table right */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Left: Bar chart */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 10, backgroundColor: '#66bb6a', opacity: 0.8, borderRadius: 0.5 }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{primaryLabel}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 10, backgroundColor: '#e3f2fd', border: '1px dashed #90caf9', borderRadius: 0.5 }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{comparisonLabel} Avg</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderLeft: '2.5px solid #212121', opacity: 0.7 }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>100%</Typography>
            </Box>
          </Box>

          {rows.map((row) => {
            const fac = row.facilityPct ?? 0;
            const st = row.statePct ?? 0;
            const facWidth = Math.min(fac, MAX_PCT) / MAX_PCT * 100;
            const stWidth = Math.min(st, MAX_PCT) / MAX_PCT * 100;
            const markerLeft = (100 / MAX_PCT) * 100;
            const isBelow = fac < 100;
            return (
              <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, width: 40, color: 'text.secondary', flexShrink: 0 }}>
                  {row.label}
                </Typography>
                <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                  <Box sx={{
                    position: 'absolute', left: 0, top: 3, bottom: 3,
                    width: `${stWidth}%`, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px dashed #90caf9',
                  }} />
                  <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${facWidth}%`, backgroundColor: isBelow ? '#ef5350' : '#66bb6a',
                    opacity: 0.8, borderRadius: 1, transition: 'width 0.3s ease',
                  }} />
                  <Tooltip title="100% = facility staffed exactly to the acuity-adjusted benchmark (STM study) for this staff category" arrow placement="top">
                    <Box sx={{
                      position: 'absolute', left: `${markerLeft}%`, top: -3, bottom: -3,
                      width: 2.5, backgroundColor: '#212121', opacity: 0.7, zIndex: 2,
                      cursor: 'help', ml: '-1px', px: '3px', mx: '-3px',
                    }} />
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Right: Table */}
        <Box sx={{ flexShrink: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minWidth: 260 }}>
          {/* Table header */}
          <Box sx={{ display: 'flex', backgroundColor: '#f5f5f5', borderBottom: '2px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 60, ...cellSx, borderBottom: 'none', borderRight: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>&nbsp;</Typography>
            </Box>
            <Box sx={{ width: 90, ...cellSx, borderBottom: 'none', borderRight: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{primaryLabel}</Typography>
            </Box>
            <Box sx={{ width: 90, ...cellSx, borderBottom: 'none' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>{comparisonLabel}</Typography>
            </Box>
          </Box>
          {/* Table rows */}
          {rows.map((row, idx) => {
            const fac = row.facilityPct ?? 0;
            const isBelow = fac < 100;
            const isLast = idx === rows.length - 1;
            return (
              <Box key={row.label} sx={{ display: 'flex', borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider' }}>
                <Box sx={{ width: 60, px: 1.5, py: 0.75, borderRight: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>{row.label}</Typography>
                </Box>
                <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right', borderRight: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: isBelow ? 'error.main' : 'success.main' }}>
                    {fmt(row.facilityPct)}
                  </Typography>
                </Box>
                <Box sx={{ width: 90, px: 1.5, py: 0.75, textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1976d2' }}>
                    {fmt(row.statePct)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default AcuityExpandPanel;
